import { useCallback, useEffect, useRef, type Dispatch } from 'react';
import type liffType from '@line/liff';
import type {
  CustomerProfile,
  CheckStatusResponse,
  SignUpRequest,
  SignInResponse,
  UpdateProfileRequest,
} from '../types/customer';
import * as api from '../utils/customerServiceApi';
import { isIdTokenExpiredError } from '../utils/customerServiceApi';
import { registerAuthInterceptorCallbacks } from '../lib/axiosCustomerService';
import { registerOrderServiceInterceptorCallbacks } from '../lib/axiosOrderService';
import type { AuthAction } from './authReducer';

const TOKEN_RELOGIN_KEY = 'liff_token_relogin';
const TOKEN_RELOGIN_TTL = 30_000;

interface UseAuthCallbacksParams {
  liff: typeof liffType | null;
  isLoggedIn: boolean;
  isReady: boolean;
  liffIdToken: string | null;
  accessToken: string | null;
  dispatch: Dispatch<AuthAction>;
}

export function useAuthCallbacks({
  liff,
  isLoggedIn,
  isReady,
  liffIdToken,
  accessToken,
  dispatch,
}: UseAuthCallbacksParams) {
  const refreshPromiseRef = useRef<Promise<SignInResponse | null> | null>(null);
  const restoreRef = useRef(false);
  const isRedirectingRef = useRef(false);

  // Register axios interceptor callbacks
  useEffect(() => {
    const onSuccess = (result: SignInResponse) => {
      dispatch({ type: 'SET_TOKENS', accessToken: result.access_token, customer: result.data });
    };
    const onFailure = () => {
      dispatch({ type: 'CLEAR_AUTH' });
    };
    registerAuthInterceptorCallbacks(onSuccess, onFailure);
    registerOrderServiceInterceptorCallbacks(onSuccess, onFailure);
    return () => {
      registerAuthInterceptorCallbacks(null, null);
      registerOrderServiceInterceptorCallbacks(null, null);
    };
  }, [dispatch]);

  const handleTokenExpired = useCallback(() => {
    if (isRedirectingRef.current) return;
    const prev = sessionStorage.getItem(TOKEN_RELOGIN_KEY);
    if (prev) {
      const elapsed = Date.now() - Number(prev);
      if (elapsed < TOKEN_RELOGIN_TTL) {
        console.warn('LINE ID token still expired after re-login — giving up');
        sessionStorage.removeItem(TOKEN_RELOGIN_KEY);
        dispatch({ type: 'SET_ERROR', error: 'LINE session expired. Please close and reopen this page from LINE.' });
        return;
      }
      sessionStorage.removeItem(TOKEN_RELOGIN_KEY);
    }
    isRedirectingRef.current = true;
    sessionStorage.setItem(TOKEN_RELOGIN_KEY, String(Date.now()));
    console.warn('LINE ID token expired — logging out then re-login');
    if (liff) {
      try { liff.logout(); } catch { /* already logged out */ }
      liff.login({ redirectUri: window.location.href });
    }
  }, [liff, dispatch]);

  const getIdToken = useCallback((): string | null => {
    if (!liff || !isLoggedIn || !isReady) return null;
    if (liffIdToken) return liffIdToken;
    try {
      const token = liff.getIDToken();
      if (token) return token;
    } catch { /* ignore */ }
    try {
      const at = liff.getAccessToken();
      if (at) {
        console.warn('Using LIFF access token as fallback (ID token unavailable). Enable "openid" scope in LINE Console for proper ID tokens.');
        return at;
      }
    } catch { /* ignore */ }
    console.warn('No LINE token available. Ensure "openid" scope is enabled in LINE Developers Console → LIFF app settings.');
    return null;
  }, [liff, isLoggedIn, isReady, liffIdToken]);

  // Auto-restore session on page load/refresh
  useEffect(() => {
    if (!isReady || restoreRef.current) return;
    restoreRef.current = true;

    const restoreSession = async () => {
      dispatch({ type: 'SET_LOADING', loading: true });
      dispatch({ type: 'SET_ERROR', error: null });

      try {
        const result = await api.refreshToken();
        dispatch({ type: 'SET_TOKENS', accessToken: result.access_token, customer: result.data });
        sessionStorage.removeItem(TOKEN_RELOGIN_KEY);
        return;
      } catch { /* Refresh token not available or expired */ }

      if (isLoggedIn) {
        const liffToken = liff ? liff.getIDToken() : null;
        const idToken = liffIdToken ?? liffToken;
        if (idToken) {
          let statusResult = null;
          try {
            statusResult = await api.checkStatus(idToken);
          } catch (err) {
            if (isIdTokenExpiredError(err)) {
              handleTokenExpired();
              return;
            }
          }
          if (statusResult) {
            dispatch({ type: 'SET_CUSTOMER_STATUS', status: statusResult });
            const isActive = statusResult.exists && (statusResult.status === 'active' || statusResult.status === 'in_monitor');
            if (isActive) {
              try {
                const signInResult = await api.signIn(idToken);
                dispatch({ type: 'SET_TOKENS', accessToken: signInResult.access_token, customer: signInResult.data });
                sessionStorage.removeItem(TOKEN_RELOGIN_KEY);
              } catch { /* Sign in failed */ }
            }
          }
        }
      }
    };

    restoreSession().then(() => {
      dispatch({ type: 'SET_INITIALIZED' });
      dispatch({ type: 'SET_LOADING', loading: false });
    }).catch(() => {
      dispatch({ type: 'SET_INITIALIZED' });
      dispatch({ type: 'SET_LOADING', loading: false });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady]);

  const checkUserStatus = useCallback(async (): Promise<CheckStatusResponse | null> => {
    const idToken = getIdToken();
    if (!idToken) {
      dispatch({ type: 'SET_ERROR', error: 'No LINE ID token available' });
      return null;
    }
    dispatch({ type: 'SET_LOADING', loading: true });
    dispatch({ type: 'SET_ERROR', error: null });

    let result: CheckStatusResponse | null = null;
    let failed = false;
    try {
      const data = await api.checkStatus(idToken);
      dispatch({ type: 'SET_CUSTOMER_STATUS', status: data });
      result = data;
    } catch (err) {
      failed = true;
      if (isIdTokenExpiredError(err)) {
        dispatch({ type: 'SET_LOADING', loading: false });
        handleTokenExpired();
        return null;
      }
      const message = err instanceof Error ? err.message : 'Failed to check status';
      dispatch({ type: 'SET_ERROR', error: message });
    }
    dispatch({ type: 'SET_LOADING', loading: false });
    return failed ? null : result;
  }, [getIdToken, handleTokenExpired, dispatch]);

  const signIn = useCallback(async (): Promise<SignInResponse | null> => {
    const idToken = getIdToken();
    if (!idToken) {
      dispatch({ type: 'SET_ERROR', error: 'No LINE ID token available' });
      return null;
    }
    dispatch({ type: 'SET_LOADING', loading: true });
    dispatch({ type: 'SET_ERROR', error: null });

    let result: SignInResponse | null = null;
    try {
      const data = await api.signIn(idToken);
      dispatch({ type: 'SET_TOKENS', accessToken: data.access_token, customer: data.data });
      result = data;
    } catch (err) {
      if (isIdTokenExpiredError(err)) {
        dispatch({ type: 'SET_LOADING', loading: false });
        handleTokenExpired();
        return null;
      }
      const message = err instanceof Error ? err.message : 'Sign in failed';
      dispatch({ type: 'SET_ERROR', error: message });
    }
    dispatch({ type: 'SET_LOADING', loading: false });
    return result;
  }, [getIdToken, handleTokenExpired, dispatch]);

  const signUp = useCallback(async (
    data: Omit<SignUpRequest, 'id_token'>,
  ): Promise<{ pending: boolean } | null> => {
    const idToken = getIdToken();
    if (!idToken) {
      dispatch({ type: 'SET_ERROR', error: 'No LINE ID token available' });
      return null;
    }
    dispatch({ type: 'SET_LOADING', loading: true });
    dispatch({ type: 'SET_ERROR', error: null });

    let outcome: { pending: boolean } | null = null;
    try {
      const result = await api.signUp({ ...data, id_token: idToken });
      if (result.response) {
        dispatch({ type: 'SET_TOKENS', accessToken: result.response.access_token, customer: result.response.data });
      }
      if (result.pending) {
        dispatch({ type: 'SET_CUSTOMER_STATUS', status: { exists: true, status: 'inactive' } });
      }
      outcome = { pending: result.pending };
    } catch (err) {
      if (isIdTokenExpiredError(err)) {
        dispatch({ type: 'SET_LOADING', loading: false });
        handleTokenExpired();
        return null;
      }
      const message = err instanceof Error ? err.message : 'Sign up failed';
      dispatch({ type: 'SET_ERROR', error: message });
      dispatch({ type: 'SET_LOADING', loading: false });
      throw err;
    }
    dispatch({ type: 'SET_LOADING', loading: false });
    return outcome;
  }, [getIdToken, handleTokenExpired, dispatch]);

  const signOut = useCallback(async () => {
    if (accessToken) {
      try {
        await api.signOut(accessToken);
      } catch { /* Ignore sign-out errors */ }
    }
    dispatch({ type: 'SIGN_OUT' });
  }, [accessToken, dispatch]);

  const refreshAuth = useCallback(async (): Promise<SignInResponse | null> => {
    if (refreshPromiseRef.current) return refreshPromiseRef.current;

    const promise = (async () => {
      dispatch({ type: 'SET_LOADING', loading: true });
      dispatch({ type: 'SET_ERROR', error: null });

      let result: SignInResponse | null = null;
      try {
        const data = await api.refreshToken();
        dispatch({ type: 'SET_TOKENS', accessToken: data.access_token, customer: data.data });
        result = data;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Token refresh failed';
        dispatch({ type: 'SET_ERROR', error: message });
      }
      dispatch({ type: 'SET_LOADING', loading: false });
      refreshPromiseRef.current = null;
      return result;
    })();

    refreshPromiseRef.current = promise;
    return promise;
  }, [dispatch]);

  const fetchProfile = useCallback(async (): Promise<CustomerProfile | null> => {
    if (!accessToken) {
      dispatch({ type: 'SET_ERROR', error: 'Not authenticated' });
      return null;
    }
    dispatch({ type: 'SET_LOADING', loading: true });
    dispatch({ type: 'SET_ERROR', error: null });

    let profile: CustomerProfile | null = null;
    try {
      profile = await api.getCustomerProfile(accessToken);
      dispatch({ type: 'SET_CUSTOMER', customer: profile });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch profile';
      dispatch({ type: 'SET_ERROR', error: message });
    }
    dispatch({ type: 'SET_LOADING', loading: false });
    return profile;
  }, [accessToken, dispatch]);

  const updateProfile = useCallback(async (data: UpdateProfileRequest) => {
    if (!accessToken) {
      dispatch({ type: 'SET_ERROR', error: 'Not authenticated' });
      return;
    }
    dispatch({ type: 'SET_LOADING', loading: true });
    dispatch({ type: 'SET_ERROR', error: null });

    let thrownErr: unknown = null;
    try {
      await api.updateCustomerProfile(accessToken, data);
      const profile = await api.getCustomerProfile(accessToken);
      dispatch({ type: 'SET_CUSTOMER', customer: profile });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update profile';
      dispatch({ type: 'SET_ERROR', error: message });
      thrownErr = err;
    }
    dispatch({ type: 'SET_LOADING', loading: false });
    if (thrownErr !== null) throw thrownErr;
  }, [accessToken, dispatch]);

  const clearAuthError = useCallback(() => dispatch({ type: 'SET_ERROR', error: null }), [dispatch]);

  return {
    checkUserStatus,
    signIn,
    signUp,
    signOut,
    refreshAuth,
    fetchProfile,
    updateProfile,
    clearAuthError,
  };
}
